import React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import type { CuratorOfferTemplate } from '../../utils/curatorOfferLibrary';
import { summarizeOffer } from '../../utils/curatorOfferLibrary';
import { countValidationErrors, validateOfferTerms } from '../../utils/offerValidation';

interface OfferLibrarySidebarProps {
  offers: CuratorOfferTemplate[];
  activeOfferId: string | null;
  unassignedCount: number;
  linkedProfileCount: number;
  onSelectOffer: (offerId: string) => void;
  onCreateOffer: () => void;
}

const OfferLibrarySidebar: React.FC<OfferLibrarySidebarProps> = ({
  offers,
  activeOfferId,
  unassignedCount,
  linkedProfileCount,
  onSelectOffer,
  onCreateOffer,
}) => {
  const assignedCount = linkedProfileCount - unassignedCount;

  return (
    <aside className="offer-library-sidebar">
      <div className="offer-library-sidebar__header">
        <h2>Your offers</h2>
        <p>Create separate packages for different profile groups.</p>
      </div>

      <div className="offer-library-sidebar__stats">
        <div className="offer-library-sidebar__stat">
          <strong>{offers.length}</strong>
          <span>Offers</span>
        </div>
        <div className="offer-library-sidebar__stat">
          <strong>{assignedCount}</strong>
          <span>Assigned</span>
        </div>
        <div className="offer-library-sidebar__stat">
          <strong>{unassignedCount}</strong>
          <span>Unassigned</span>
        </div>
      </div>

      <div className="offer-library-sidebar__list">
        {offers.map(offer => {
          const summary = summarizeOffer(offer);
          const isActive = offer.id === activeOfferId;
          const termsComplete = countValidationErrors(validateOfferTerms(offer.terms)) === 0;
          const isComplete = termsComplete && offer.profileIds.length > 0;

          return (
            <button
              key={offer.id}
              type="button"
              className={`offer-library-card ${isActive ? 'is-active' : ''}`}
              onClick={() => onSelectOffer(offer.id)}
            >
              <div className="offer-library-card__top">
                <span className="offer-library-card__label">{offer.label}</span>
                <span
                  className={`offer-library-card__badge ${isComplete && offer.profileIds.length ? 'is-complete' : 'is-draft'}`}
                >
                  {isComplete && offer.profileIds.length ? 'Ready' : 'Draft'}
                </span>
              </div>
              <span className="offer-library-card__meta">
                {summary.profileCount} profile{summary.profileCount === 1 ? '' : 's'}
                {summary.rate ? ` · $${summary.rate}/1K` : ''}
              </span>
              {summary.genres.length ? (
                <span className="offer-library-card__genres">{summary.genres.join(', ')}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <button type="button" className="offer-library-sidebar__new" onClick={onCreateOffer}>
        <PlusOutlined /> New offer
      </button>

      {unassignedCount > 0 ? (
        <div className="offer-library-sidebar__hint">
          {unassignedCount} profile{unassignedCount === 1 ? '' : 's'} not assigned to an offer yet.
          Assign them before saving.
        </div>
      ) : null}
    </aside>
  );
};

export default OfferLibrarySidebar;
